import { useEffect, useState } from 'react';
import { listing, type ListingPhotoCollection } from '@/src/lib/api/endpoints/listing';

const photoCollectionCache = new Map<string, ListingPhotoCollection>();
const photoCollectionPromises = new Map<string, Promise<ListingPhotoCollection>>();

type ListingPhotoCollectionState = {
  data?: ListingPhotoCollection;
  error?: Error;
  isLoading: boolean;
};

type UseListingPhotoCollectionOptions = {
  enabled?: boolean;
};

async function fetchListingPhotoCollection(maloneLotIdEncoded: string): Promise<ListingPhotoCollection> {
  const unitResult = await listing.fetchListingPhotoCollectionByMaloneLotIdEncoded(
    maloneLotIdEncoded,
    { type: 'unit' },
  );
  if (unitResult.medPhotoUrls.length > 0) return unitResult;

  return listing.fetchListingPhotoCollectionByMaloneLotIdEncoded(maloneLotIdEncoded, {
    type: 'building',
  });
}

function getListingPhotoCollection(maloneLotIdEncoded: string): Promise<ListingPhotoCollection> {
  const cached = photoCollectionCache.get(maloneLotIdEncoded);
  if (cached) return Promise.resolve(cached);

  const existingPromise = photoCollectionPromises.get(maloneLotIdEncoded);
  if (existingPromise) return existingPromise;

  const promise = fetchListingPhotoCollection(maloneLotIdEncoded)
    .then((data) => {
      photoCollectionCache.set(maloneLotIdEncoded, data);
      return data;
    })
    .finally(() => {
      photoCollectionPromises.delete(maloneLotIdEncoded);
    });

  photoCollectionPromises.set(maloneLotIdEncoded, promise);
  return promise;
}

export function useListingPhotoCollection(
  maloneLotIdEncoded: string | null | undefined,
  options?: UseListingPhotoCollectionOptions,
): ListingPhotoCollectionState {
  const enabled = options?.enabled !== false && Boolean(maloneLotIdEncoded);
  const [state, setState] = useState<ListingPhotoCollectionState>(() => ({
    data: maloneLotIdEncoded ? photoCollectionCache.get(maloneLotIdEncoded) : undefined,
    isLoading: false,
  }));

  useEffect(() => {
    if (!maloneLotIdEncoded) {
      setState({ isLoading: false });
      return;
    }

    const cached = photoCollectionCache.get(maloneLotIdEncoded);
    if (!enabled) {
      setState({ data: cached, isLoading: false });
      return;
    }

    if (cached) {
      setState({ data: cached, isLoading: false });
      return;
    }

    let cancelled = false;
    setState((current) => ({ ...current, error: undefined, isLoading: true }));

    getListingPhotoCollection(maloneLotIdEncoded)
      .then((data) => {
        if (!cancelled) setState({ data, isLoading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            error: error instanceof Error ? error : new Error('Failed to load listing photos'),
            isLoading: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, maloneLotIdEncoded]);

  return state;
}
